<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useDateFormat } from '@vueuse/core'
  import { useDraftsMenuStore } from '@/stores/list-item-menu'
  import { useDraftsStore } from '@/stores/drafts'
  import { useDraftsBarStore } from '@/stores/floating-bars'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { usePrintBooksStore } from '@/stores/print-books'
  import { useKindleBooksStore } from '@/stores/kindle-books'
  import { useRouter } from 'vue-router'
  import type { TDraft } from '@/types/drafts'
  import { amazonCoverImagePath } from '@/helpers/coverImage'
  import DesHover from '@/components/DesHover.vue'
  import DesHoverMenu from '@/components/DesHoverMenu.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'
  import DesEditName from '@/components/DesEditName.vue'
  import DesCheckbox from '@/components/form/DesCheckbox.vue'

  const props = defineProps<{
    item: TDraft
    disableCheckboxes?: boolean
    presentation?: 'list' | 'grid'
  }>()

  const drafts = useDraftsStore()
  const bar = useDraftsBarStore()
  const snack = useSnackbarStore()
  const printBooks = usePrintBooksStore()
  const kindleBooks = useKindleBooksStore()
  const router = useRouter()

  const { open } = useDraftsMenuStore()

  const formatDate = (date: string) => useDateFormat(date, 'MMMM DD, YYYY', { locales: 'en-US' })
  const tile = ref<HTMLElement | null>(null)
  const checked = ref<boolean>(bar.is(props.item))
  const isHovered = ref(false)

  const edit = (draft: TDraft) => {
    const transcriptionInProgress = 'The transcription is still in progress'
    const from = 'dashboard-click'
    if (draft.type === 'Standard') {
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else if (draft.type === 'PrintBook') {
      const lastStep = printBooks.getStepURL(draft.print_book?.id || 0)
      if (lastStep) {
        return router.push(lastStep)
      }
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else if (draft.type === 'Audio' && draft.status === 'Failed') {
      snack.add('Transcription failed', { type: 'danger' })
    } else if (draft.type === 'Audio' && draft.status !== 'In progress') {
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else if (draft.type === 'Audio') {
      snack.add(transcriptionInProgress)
    } else if (draft.type === 'TemiJob' && draft.status === 'Transcribed') {
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else if (draft.type === 'TemiJob' && draft.status === 'Failed') {
      snack.add('Transcription failed', { type: 'danger' })
    } else if (draft.type === 'TemiJob' && draft.status === 'Downloading') {
      snack.add('Your video is downloading and will be transcribed shortly')
    } else if (draft.type === 'TemiJob') {
      snack.add(transcriptionInProgress)
    } else if (draft.type === 'AudioBook') {
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else if (draft.type === 'KindleBook') {
      const lastStep = kindleBooks.getStepURL(draft.kindle_book?.id || 0)
      if (lastStep) {
        return router.push(lastStep)
      }
      return drafts.goToEditDraft(draft.id, draft.type, from)
    } else {
      console.log('[not handled] edit draft', draft.type)
    }
  }

  const rename = async (draft: TDraft, name: string) => {
    const oldName = draft.name
    // the home page passes a recent item, which is a row of no listing, so the given row is written
    // directly as well as through the listings
    draft.name = name
    drafts.patchDraft(draft.id, { name })
    const res = await drafts.rename(draft.id, name)
    if (!res) {
      snack.add('Could not rename this document')
      draft.name = oldName
      drafts.patchDraft(draft.id, { name: oldName })
    }
  }

  // covers generated before the -thumb companion existed only have the full image
  const coverThumbMissing = ref<boolean>(false)

  const src = computed(() => {
    const hasPrintBookCover = props.item.print_book?.amazon_cover_id && props.item.type === 'PrintBook'
    const hasKindleBookCover = props.item.kindle_book?.amazon_cover_id && props.item.type === 'KindleBook'

    if ((hasPrintBookCover || hasKindleBookCover) && isHovered.value) {
      const coverId = props.item.print_book?.amazon_cover_id ?? props.item.kindle_book?.amazon_cover_id
      const cover = props.item.print_book?.cover ?? props.item.kindle_book?.cover
      const path = coverThumbMissing.value ? cover?.image_url : cover?.thumb_url

      return location.origin + amazonCoverImagePath(coverId ?? 0, path, !coverThumbMissing.value)
    }

    if (props.item.thumbnail_src) {
      return location.origin + props.item.thumbnail_src
    }

    return location.origin + '/assets/images/drafts/' + props.item.thumbnail
  })

  function onThumbError() {
    if (!coverThumbMissing.value && src.value.includes('-thumb.jpg')) {
      coverThumbMissing.value = true
    }
  }
  const icon = computed(() => {
    const iconsMap: { [t: string]: string } = {
      AudioBook: 'podcast',
      Audio: 'video',
      TemiJob: 'video',
      Standard: 'doc',
      PrintBook: 'book',
      KindleBook: 'kindle',
    }
    return '/dashboard/new-project/ico-from-' + iconsMap[props.item.type] + '.png'
  })

  const placeholder = computed(() => {
    const iconsMap = {
      AudioBook: 'fa-headphones',
      Audio: 'fa-circle-play',
      TemiJob: 'fa-circle-play',
      Standard: 'fa-file',
      PrintBook: 'fa-book',
      KindleBook: 'fa-tablet',
    }
    return iconsMap[props.item.type]
  })

  const statusBadge = computed<{ label: string; failed: boolean } | null>(() => {
    if (props.item.type !== 'TemiJob' && props.item.type !== 'Audio') {
      return null
    }
    if (props.item.status === 'Downloading') {
      return { label: 'Downloading…', failed: false }
    }
    if (props.item.status === 'In progress') {
      return { label: 'Processing…', failed: false }
    }
    if (props.item.status === 'Failed') {
      return { label: 'Transcription failed', failed: true }
    }
    return null
  })

  const date = computed(() => (props.item.updated_at ? formatDate(props.item.updated_at).value : ''))

  const importType = computed(() => {
    if (!props.item.import_type) {
      return ''
    }

    try {
      const type = JSON.parse(props.item.import_type)
      return drafts.getLabel(type.type || type.subType)
    } catch {
      return ''
    }
  })

  watch(
    () => bar.list.length,
    () => (checked.value = bar.is(props.item)),
  )
</script>

<template>
  <div
    class="flex items-center gap-6 xl:gap-12 2xl:gap-24 px-4 py-6 border-b border-black-90 hover:bg-white-gray cursor-pointer"
    :class="{ 'outline-solid outline-1 outline-blue-50 rounded-sm': checked }"
    v-if="presentation == 'list'"
    @click.stop.prevent="edit(item)"
  >
    <div class="flex items-center gap-2 grow">
      <DesCheckbox v-model="checked" @click.stop.prevent="bar.toggle(item)" v-if="props.disableCheckboxes !== true" />
      <img :src="icon" class="h-8" alt="" />
      <DesEditName :name="item.name || ''" @edit="(newValue: string) => rename(item, newValue)" />
      <span
        v-if="statusBadge"
        class="flex items-center gap-1.5 text-xs whitespace-nowrap"
        :class="statusBadge.failed ? 'text-red-50' : 'text-black-40'"
      >
        <des-icon v-if="statusBadge.failed" icon="fa-solid fa-circle-exclamation" class="shrink-0 w-3 h-3" />
        <span v-else class="shrink-0 h-3 w-3 border-2 border-blue-90 border-t-blue-50 rounded-full animate-spin"></span>
        {{ statusBadge.label }}
      </span>
    </div>
    <div class="w-40 shrink-0">{{ date }}</div>
    <div class="w-40 shrink-0 -my-2">{{ importType }}</div>
    <div class="w-12 shrink-0 flex items-center" ref="tile">
      <des-icon
        class="w-icon h-icon box-content cursor-pointer p-1 rounded-sm hover:bg-black-90"
        icon="fa-light fa-ellipsis-vertical"
        @click.stop="tile && open(item, tile)"
      />
    </div>
  </div>
  <div class="flex flex-col gap-1.5" ref="tile" v-else @mouseenter="isHovered = true" @mouseleave="isHovered = false">
    <div
      class="relative h-52 px-4 pt-4 bg-white-gray rounded-xl justify-center items-start inline-flex overflow-hidden cursor-pointer border"
      :class="checked ? 'border-blue-50' : 'border-transparent'"
    >
      <div
        v-if="statusBadge"
        class="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow-sm text-xs whitespace-nowrap"
        :class="statusBadge.failed ? 'text-red-50' : 'text-black-40'"
      >
        <des-icon v-if="statusBadge.failed" icon="fa-solid fa-circle-exclamation" class="shrink-0 w-3 h-3" />
        <span v-else class="shrink-0 h-3 w-3 border-2 border-blue-90 border-t-blue-50 rounded-full animate-spin"></span>
        {{ statusBadge.label }}
      </div>
      <div
        class="relative h-64 flex-col justify-start items-center inline-flex"
        v-if="item.type === 'Audio' || item.type === 'TemiJob' || item.type === 'AudioBook'"
      >
        <img
          v-if="item.thumbnail"
          class="rounded-md max-h-full max-w-full w-auto h-auto shadow-sm"
          :src="src"
          @error="onThumbError"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
        <div
          class="absolute h-64 flex-col justify-start items-center inline-flex mx-6 w-52 rounded-md shadow-sm pt-14"
          :class="!item.thumbnail ? 'bg-white' : 'bg-transparent'"
        >
          <des-icon :icon="`fa-light ${placeholder}`" class="w-20 h-20 text-black-40" />
        </div>
      </div>

      <div class="h-64 flex-col justify-start items-center inline-flex" v-else>
        <img
          v-if="item.thumbnail"
          class="rounded-md max-h-full max-w-full w-auto h-auto shadow-sm"
          :src="src"
          @error="onThumbError"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
        <div
          class="h-64 flex-col justify-start items-center inline-flex bg-white mx-6 w-52 rounded-md shadow-sm pt-14"
          v-else
        >
          <des-icon :icon="`fa-light ${placeholder}`" class="w-20 h-20 text-black-40" />
        </div>
      </div>

      <DesHover data-sortable-handler @click.stop="edit(item)">
        <DesHoverMenu>
          <DesButtonIcon @click.stop="tile && open(item, tile)" icon="fa-light fa-ellipsis" />
        </DesHoverMenu>
        <DesCheckbox v-model="checked" @click.stop.prevent="bar.toggle(item)" v-if="props.disableCheckboxes !== true" />
      </DesHover>
      <DesHover @click.stop.prevent="bar.toggle(item)" v-if="bar.list.length > 0" locked>
        <DesCheckbox v-model="checked" v-if="props.disableCheckboxes !== true" />
      </DesHover>
    </div>
    <div>
      <DesEditName :name="item.name || ''" @edit="(newValue: string) => rename(item, newValue)" />
      <div class="mt-1 text-black-40 text-xs leading-none">{{ date }}</div>
    </div>
  </div>
</template>
